import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IconButton, CircularProgress } from '@mui/material';
import BadgeAvatar from '@src/components/BadgeAvatar';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import actions from '@src/redux/actions';
import {
  BLOCK_STATUS,
  BLOCK_STATUS_LABEL,
  CONVERTING_STATUS,
  TRANSLATION_STATUS,
} from '@src/constants/project';
import Tooltip from '@src/components/Tooltip';
import DropdownCustom from '@src/new-components/Dropdown';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useTrackProjectFilterApplied from '@src/hooks/useTrackProjectFilterApplied';
import { MOE_FILTER_TYPE } from '@src/constants/moengage';
import useExportHistoryEnabled from '@src/hooks/useExportHistoryEnabled';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { HISTORY_SCOPE, HISTORY_LABEL } from '@src/constants/history';
import { getExportingState, isVideoSource } from '@src/services/project';
import pluralize from '@src/utils/pluralize';
import { StyledItemVoice, StyledToolbar } from './index.style';
import ConfigMultiBlock from './ConfigMultiBlock';
import TranslationDropdown from './TranslationDropdown';

const Toolbar = ({
  handleConvertBlockSelected,
  canDownloadBlockAudio,
  setDeleteDialog,
  filterBlockResults,
  onOpenTranslateDialog,
  onOpenTranslateOtherLanguage,
  projectId,
  isSkeletonUI = false,
}) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);
  const trackFilterApplied = useTrackProjectFilterApplied();
  const selectedBlockIds = useSelector(
    (state) => state.project.selectedBlockIds,
  );
  const currentFilteringSpeakerId = useSelector(
    (state) => state.project.currentFilteringSpeakerId,
  );
  const speakers = useSelector((state) => state.project.speakers);
  const isBlockUIImprovement = getFeatureValue(
    FEATURE_KEYS.PROJECT_TTS_IMPROVE_BLOCK_UI,
    { userId: user?.id, email: user?.email },
  );
  const speakerConfigEnabled = getFeatureValue(FEATURE_KEYS.SPEAKER_CONFIG, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const undoRedoEnabled = getFeatureValue(
    FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );

  // History meta for a filter change so undo/redo can step through filters.
  // The accompanying setStartFilterBlock dispatch stays untagged (view-only).
  const filterHistoryOptions = (label) =>
    undoRedoEnabled
      ? { history: { commit: true, scope: HISTORY_SCOPE.DUBBING, label } }
      : undefined;

  const isAiTranslateEnabled = getFeatureValue(FEATURE_KEYS.AI_TRANSLATE, {
    userId: user?.id,
    email: user?.email,
  });

  // Per-field selectors, not the whole `project` slice, so an unrelated change
  // (currentBlockId, isPlaying) doesn't re-render the toolbar.
  const currentFilteringStatus = useSelector(
    (state) => state.project.currentFilteringStatus,
  );
  const multiBlock = useSelector((state) => state.project.multiBlock);
  const translation = useSelector((state) => state.project.translation);
  const isProjectSynthesizing = useSelector(
    (state) => state.project.isProjectSynthesizing,
  );
  const isPreProcessingSynthesis = useSelector(
    (state) => state.project.isPreProcessingSynthesis,
  );
  const exports = useSelector((state) => state.project.exports);
  const isDownloading = useSelector((state) => state.project.isDownloading);
  const product = useSelector((state) => state.project.product);
  const source = useSelector((state) => state.project.source);
  const { blocks, blockIds } = multiBlock;

  // A synchronous download is built from the blocks as they stand, so what
  // edits them — translating, which rewrites every block's text — waits for it
  // to land. The filters are left alone: they only narrow what the list shows
  // and change nothing the running export is assembling.
  //
  // An async export is different behind the export-history flag: the backend
  // snapshots the project when the export starts, so a later translation cannot
  // reach the file it is building. `getExportingState` reports both kinds, so
  // only the async half is released here.
  const exportHistoryEnabled = useExportHistoryEnabled();
  const { isExporting } = getExportingState({
    exports,
    isDownloading,
    product,
  });
  const isDownloadInFlight =
    isDownloading || (isExporting && !exportHistoryEnabled);

  const hasTranslationSuccess =
    translation?.status === TRANSLATION_STATUS.SUCCESS;
  const hasNoBlocks = Object.keys(blocks).length === 0;
  const allBlocksHaveNoText =
    !hasNoBlocks &&
    Object.values(blocks).every((block) => !block?.text?.trim());

  // Translating rewrites every block's text, so it must not run while any block
  // is being synthesized — the synthesis would land on text that no longer
  // exists. `isProjectSynthesizing` alone does not see this: only the legacy
  // whole-project SRT flow ever sets it, while per-block and multi-block
  // create-audio mark the blocks themselves.
  const hasSynthesizingBlock = Object.values(blocks).some(
    (block) => block?.status === CONVERTING_STATUS.IN_PROGRESS,
  );

  const isDisabledTranslateLanguage =
    isProjectSynthesizing ||
    isPreProcessingSynthesis ||
    isDownloadInFlight ||
    hasSynthesizingBlock ||
    hasNoBlocks ||
    allBlocksHaveNoText;

  const numBlockForEachStatus = useMemo(() => {
    // Make an object for each key is block status and value is number of block for that status
    const blockStatusCount = Object.keys(BLOCK_STATUS).reduce((acc, key) => {
      let arrOfBlocks = Object.values(blocks);
      if (currentFilteringSpeakerId) {
        arrOfBlocks = arrOfBlocks.filter(
          (block) => block.speakerId === currentFilteringSpeakerId,
        );
      }
      if (key === BLOCK_STATUS.ALL) {
        acc[key] = arrOfBlocks.length;
        return acc;
      }

      const arr = arrOfBlocks.filter((block) => block.blockStatus === key);

      acc[key] = arr.length;
      return acc;
    }, {});

    return blockStatusCount;
  }, [blocks, currentFilteringSpeakerId]);

  const getNumberOfSpeakers = useCallback(
    (speakerId) => {
      if (currentFilteringStatus === BLOCK_STATUS.ALL) {
        return true;
      }

      const blockGroupByStatus = Object.keys(BLOCK_STATUS).reduce(
        (acc, key) => {
          let arrOfBlocks = Object.values(blocks);
          if (currentFilteringStatus) {
            arrOfBlocks = arrOfBlocks.filter(
              (block) => block.blockStatus === currentFilteringStatus,
            );
          }

          if (key === BLOCK_STATUS.ALL) {
            acc[key] = arrOfBlocks;
            return acc;
          }

          const arr = arrOfBlocks.filter((block) => block.blockStatus === key);

          acc[key] = arr;
          return acc;
        },
        {},
      );

      if (speakerId === null) {
        return Object.keys(filterBlockResults)?.length;
      }

      return (
        blockGroupByStatus?.[currentFilteringStatus]?.filter(
          (block) => block?.speakerId === speakerId,
        )?.length || 0
      );
    },
    [currentFilteringStatus, blocks, filterBlockResults],
  );

  const blockStatusOptions = useMemo(() => {
    const baseOptions = Object.values(BLOCK_STATUS_LABEL).map((item) => ({
      label: `${t(item.label)} (${numBlockForEachStatus[item.value]})`,
      value: item.value,
      disabled: numBlockForEachStatus[item.value] === 0,
    }));

    // An SRT project without a video has no per-block audio, so only text errors
    // are meaningful — cap the filter to All + Error. Video projects keep all.
    if (!isVideoSource(source)) {
      return baseOptions.filter(
        (item) =>
          item.value === BLOCK_STATUS.ALL || item.value === BLOCK_STATUS.ERROR,
      );
    }

    return baseOptions;
  }, [numBlockForEachStatus, t, source]);

  const handleChangeBlockStatus = (value) => {
    // Re-selecting the current status still refreshes the list — a block's
    // status may have changed (e.g. after converting) and no longer match.
    if (value !== currentFilteringStatus) {
      if (value !== BLOCK_STATUS.ALL)
        trackFilterApplied({
          filterType: MOE_FILTER_TYPE.STATUS,
          filterValue: value,
        });
      dispatch(
        actions.project.setFilterStatus(
          value,
          filterHistoryOptions(HISTORY_LABEL.FILTER_STATUS),
        ),
      );
    }
    dispatch(actions.project.setStartFilterBlock(true));
  };

  useEffect(
    () => () => {
      dispatch(actions.project.setFilterStatus(BLOCK_STATUS.ALL));
      dispatch(actions.project.setFilterSpeakerId(null));
    },
    [],
  );

  const speakersUsed = useMemo(() => {
    const speakerIds = new Set();
    // Always start with "All speakers"
    const uniqueSpeakers = [
      {
        id: null,
        name: 'allSpeakers',
        voice: null,
      },
    ];
    speakerIds.add(null);

    // Get all unique speakerIds from blocks
    Object.values(blocks).forEach((block) => {
      const { speakerId } = block;
      if (speakerId && !speakerIds.has(speakerId)) {
        const speaker = speakers?.find((s) => s.id === speakerId);
        if (speaker) {
          uniqueSpeakers.push(speaker);
          speakerIds.add(speakerId);
        }
      }
    });

    return uniqueSpeakers;
  }, [blocks, speakers, currentFilteringSpeakerId]);

  // Get the current filtering speaker
  const currentFilteringSpeaker = useMemo(
    () => speakersUsed?.find((item) => item?.id === currentFilteringSpeakerId),
    [speakersUsed, currentFilteringSpeakerId],
  );

  const handleRefresh = () => {
    if (!currentFilteringSpeaker) {
      dispatch(actions.project.setFilterSpeakerId(null));
    }

    dispatch(actions.project.setStartFilterBlock(true));
  };

  const startIconFilterBlockSpeaker = useMemo(() => {
    if (currentFilteringSpeakerId === null || !currentFilteringSpeaker) {
      return (
        <ManageAccountsRoundedIcon
          sx={{ width: 24, height: 24, color: COLOR.navyBlue }}
        />
      );
    }

    return (
      <BadgeAvatar
        width={16}
        smallImgWidth={10}
        img={currentFilteringSpeaker?.voice?.roundImage}
        smallImg={
          currentFilteringSpeaker?.voice?.language &&
          currentFilteringSpeaker?.voice?.language.roundImage
        }
        type="image"
      />
    );
  }, [currentFilteringSpeakerId, speakersUsed, currentFilteringSpeaker]);

  const titleFilterBlockSpeaker = useMemo(() => {
    if (!currentFilteringSpeaker || currentFilteringSpeakerId === null) {
      return t('filterBySpeaker');
    }
    return currentFilteringSpeaker?.name || t('defaultSpeakerName');
  }, [currentFilteringSpeaker, t]);

  return (
    <StyledToolbar>
      {isSkeletonUI ? (
        <div className="toolbar-loading">
          <CircularProgress
            className="toolbar-loading-spinner"
            size={20}
            thickness={5}
          />
          <Typography variant="body-14-medium" color={COLOR.blue600}>
            {t('dubbingProcessingProgress', {
              count: blockIds.length,
              unit: pluralize(blockIds.length, t('dubbingBlock'), language),
            })}
          </Typography>
        </div>
      ) : (
        <div className="toolbar">
          <ConfigMultiBlock
            handleConvertBlockSelected={handleConvertBlockSelected}
            canDownloadBlockAudio={canDownloadBlockAudio}
            setDeleteDialog={setDeleteDialog}
            filterBlockResults={filterBlockResults}
            isBlockUIImprovement={isBlockUIImprovement}
          />

          {!selectedBlockIds?.length && (
            <div className="toolbar-container">
              {isAiTranslateEnabled &&
                (hasTranslationSuccess ? (
                  <TranslationDropdown
                    projectId={projectId}
                    onOpenTranslateOtherLanguage={onOpenTranslateOtherLanguage}
                    isLocked={isDownloadInFlight}
                    hasSynthesizingBlock={hasSynthesizingBlock}
                  />
                ) : (
                  <CommonButton
                    variant="outline"
                    disabled={isDisabledTranslateLanguage}
                    onClick={() => {
                      if (isDisabledTranslateLanguage) return;
                      onOpenTranslateDialog();
                    }}
                    startIcon={
                      <TranslateRoundedIcon
                        sx={{
                          width: 20,
                          height: 20,
                          color: COLOR.indigo.default,
                        }}
                      />
                    }
                    customStyle={{
                      padding: '6px 12px',
                      borderRadius: '8px',
                      border: `1px solid ${COLOR.indigo[16]}`,
                      '&:hover': {
                        backgroundColor: COLOR.indigo[8],
                      },
                      '&:disabled': {
                        pointerEvents: 'all',
                        cursor: 'not-allowed',
                        opacity: 0.5,
                        '&:hover': {
                          backgroundColor: COLOR.white,
                          border: `1px solid ${COLOR.secondary['32']}`,
                        },
                      },
                    }}
                  >
                    <Typography
                      variant="body-14-medium"
                      color={COLOR.indigo.default}
                    >
                      {t('translateBtn')}
                    </Typography>
                  </CommonButton>
                ))}
              {speakerConfigEnabled && (
                <DropdownCustom
                  startIcon={startIconFilterBlockSpeaker}
                  onChange={(value) => {
                    const speakerId = value === null ? null : value;
                    // Re-selecting the current speaker still refreshes the list.
                    if (speakerId !== currentFilteringSpeakerId) {
                      // Speaker ids are opaque — no filter_value
                      if (speakerId)
                        trackFilterApplied({
                          filterType: MOE_FILTER_TYPE.SPEAKER,
                        });
                      dispatch(
                        actions.project.setFilterSpeakerId(
                          speakerId,
                          filterHistoryOptions(HISTORY_LABEL.FILTER_SPEAKER),
                        ),
                      );
                    }
                    dispatch(actions.project.setStartFilterBlock(true));
                  }}
                  searchable
                  searchPlaceholder={t('search')}
                  variant="block-voice"
                  options={speakersUsed?.map((item) => ({
                    label: item?.name || t('defaultSpeakerName'),
                    value: item?.id,
                    labelCustom: (
                      <StyledItemVoice>
                        <div style={{ marginRight: '8px' }}>
                          {item?.id === null ? (
                            <ManageAccountsRoundedIcon
                              sx={{
                                width: 24,
                                height: 24,
                                color: COLOR.navyBlue,
                              }}
                            />
                          ) : (
                            <BadgeAvatar
                              width={24}
                              img={item?.voice?.roundImage}
                              smallImg={
                                item?.voice?.language &&
                                item?.voice?.language.roundImage
                              }
                              type="image"
                            />
                          )}
                        </div>
                        <Typography
                          variant="body-14-medium"
                          color={COLOR.darkGray}
                        >
                          {item?.id === null
                            ? t('allSpeakers')
                            : item?.name || t('defaultSpeakerName')}
                        </Typography>
                      </StyledItemVoice>
                    ),
                    disabled: !getNumberOfSpeakers(item?.id),
                  }))}
                  title={
                    <Typography
                      variant="body-14-medium"
                      color={COLOR.indigo.default}
                    >
                      {titleFilterBlockSpeaker}
                    </Typography>
                  }
                  value={currentFilteringSpeakerId}
                />
              )}
              <DropdownCustom
                variant="block-status"
                options={blockStatusOptions}
                onChange={(value) => handleChangeBlockStatus(value)}
                title={
                  <Typography
                    variant="body-14-medium"
                    color={COLOR.indigo.default}
                  >
                    {
                      blockStatusOptions.find(
                        (item) => item.value === currentFilteringStatus,
                      )?.label
                    }
                  </Typography>
                }
                value={currentFilteringStatus}
              />
              <Tooltip
                title={t('refershFilterBlockStatus')}
                placement="bottom"
                arrow
              >
                <IconButton className="refresh-button" onClick={handleRefresh}>
                  <RefreshRoundedIcon className="refresh-icon" />
                </IconButton>
              </Tooltip>
            </div>
          )}
        </div>
      )}
    </StyledToolbar>
  );
};

export default Toolbar;
